import { Theme } from "@mui/material";
import {
  bleedByPosition,
  borderRadiusByPosition,
} from "src/utils/Company/Absences/absenceDayPositionStyles";
import { AbsenceDayPosition } from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";

export const datepickerStyles = {
  "&.MuiDateRangeCalendar-root": {
    background: "transparent",
    padding: "1.5rem 0 0",
  },

  "&.MuiMultiInputDateRangeField-root": {
    flexDirection: "column",
    gap: 2,
    padding: "1.5rem 0 0",
  },

  ".MuiDateRangeCalendar-monthContainer": {
    minHeight: "373px",
    minWidth: "336px",
    background: "white",
    padding: "0.5rem 0.75rem 0",
    marginRight: "1rem",
    border: "none",
    borderRadius: "1rem",
    boxShadow:
      "0px 4px 50px 0px rgba(166, 166, 166, 0.20), 0px 1px 1px 0px rgba(242, 242, 242, 0.25)",
  },

  ".MuiPickersSlideTransition-root": {
    minHeight: "270px",
  },

  ".MuiDayCalendar-loadingContainer": {
    position: "relative",
  },

  ".MuiInputBase-root": {
    background: "white",
  },

  ".MuiFormControl-root:nth-child(2)": {
    margin: 0,
  },

  ".MuiDateRangePickerDay-day": {
    borderRadius: "0.5rem",
  },

  ".MuiDateRangePickerDay-rangeIntervalDayHighlightStart .MuiDateRangePickerDay-day":
    {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },

  ".MuiDateRangePickerDay-rangeIntervalDayHighlightEnd .MuiDateRangePickerDay-day":
    {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },

  ".MuiDateRangePickerDay-rangeIntervalDayHighlightStart.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd .MuiDateRangePickerDay-day":
    {
      borderRadius: "0.5rem",
    },

  ".MuiDateRangePickerDay-root": {
    ":first-of-type": {
      borderTopLeftRadius: "0.5rem !important",
      borderBottomLeftRadius: "0.5rem !important",
    },

    "&:last-of-type": {
      borderTopRightRadius: "0.5rem !important",
      borderBottomRightRadius: "0.5rem !important",
    },
  },
};

// box-shadow paints the colored background + bleed for plain PickersDay elements
// where overflow:hidden prevents ::before from escaping the cell bounds.
// We use an inset shadow for the fill and a spread shadow for the bleed.
const buildBleedShadow = (
  color: string,
  bleed: { left: string; right: string }
): string => {
  // Convert bleed values like "-2px" / "0px" to numbers
  const l = parseInt(bleed.left, 10); // negative = bleed left
  const r = parseInt(bleed.right, 10); // negative = bleed right

  // A single spread box-shadow fills the cell and extends into adjacent gaps.
  // offsetX shifts the shadow right by (r - l) / 2 to cover asymmetric bleeds,
  // spread covers the larger of the two sides.
  const offsetX = (Math.abs(r) - Math.abs(l)) / 2;
  const spread = Math.max(Math.abs(l), Math.abs(r));

  return `${offsetX}px 0px 0px ${spread}px ${color}`;
};

export const getPickerStyles = (
  isRequestDay: boolean,
  bgColor?: string,
  position: AbsenceDayPosition = AbsenceDayPosition.SINGLE
) => {
  const defaultBgColor = isRequestDay
    ? bgColor ?? "transparent"
    : "transparent";
  const bleed = bleedByPosition[position];
  const borderRadius = isRequestDay
    ? borderRadiusByPosition[position]
    : "0.5rem";

  return {
    "&:hover, &:focus": {
      bgcolor: "transparent",
      borderRadius,
    },

    ".MuiDateRangePickerDay-rangeIntervalPreview": {
      bgcolor: defaultBgColor,
      borderColor: "white !important",
      borderRadius: "0.5rem !important",
      borderStyle: "solid !important",
    },

    "&.MuiPickersDay-dayWithMargin": {
      bgcolor: "transparent",
      borderColor: "white",
      borderRadius,
    },

    // For DateRangePickerDay: the inner day element carries the background.
    // overflow: visible allows the ::before bleed to escape the cell bounds.
    ".MuiDateRangePickerDay-day": {
      bgcolor: "transparent",
      borderRadius,
      position: "relative",
      overflow: "visible !important",
      zIndex: 1,
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        bottom: 0,
        left: isRequestDay ? bleed.left : "0px",
        right: isRequestDay ? bleed.right : "0px",
        backgroundColor: defaultBgColor,
        borderRadius,
        zIndex: -1,
      },
    },

    // For plain PickersDay (DatePicker / AbsenceHourPicker): sx is applied
    // directly on the root element, so we target it with & and use a negative
    // margin trick via outline instead of ::before (overflow:hidden can't be
    // overridden reliably). We use box-shadow to paint the bleed instead.
    "&.MuiPickersDay-root": {
      bgcolor: defaultBgColor,
      borderRadius,
      ...(isRequestDay && {
        boxShadow: buildBleedShadow(defaultBgColor, bleed),
      }),
    },

    "&.MuiDateRangePickerDay-hiddenDayFiller .MuiDateRangePickerDay-rangeIntervalPreview":
      {
        bgcolor: "white",
      },
  };
};

export const getRangeIntervalStyles = (
  theme: Theme,
  isExcluded: boolean,
  isCustomExcluded: boolean
) => {
  let customFontColor = "initial";

  if (isExcluded) {
    customFontColor = "text.disabled";
  } else if (isCustomExcluded) {
    customFontColor = "#546e7a !important";
  }

  return {
    "&.MuiPickersDay-root": {
      color: customFontColor,
    },

    ".MuiDateRangePickerDay-notSelectedDate": {
      color: customFontColor,
    },

    "&.MuiDateRangePickerDay-rangeIntervalDayHighlight:has(.Mui-disabled)": {
      bgcolor: "transparent !important",
    },

    ".MuiPickersDay-today": {
      borderColor: "primary.dark",
      borderRadius: "0.5rem",
    },

    "&.MuiDateRangePickerDay-root": {
      borderRadius: 0,
    },

    "&.MuiDateRangePickerDay-rangeIntervalDayHighlight .MuiDateRangePickerDay-rangeIntervalPreview .MuiDateRangePickerDay-day":
      {
        borderTop: `1.5px solid ${theme.palette.primary.main}`,
        borderBottom: `1.5px solid ${theme.palette.primary.main}`,

        "&:not(.Mui-selected)": {
          borderRadius: 0,
        },
      },
  };
};

export const getAbsenceRequestDayStyles = (color: string) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color,
  fontWeight: 700,
});
